// Image Block — URL or upload from computer
import { escapeAttr, performMediaUpload, canUploadMedia } from './blockUtils';

class ImageBlock {
  static get toolbox() {
    return {
      title: 'Image',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, readOnly }) {
    this.data = {
      url: data.url || '',
      caption: data.caption || '',
      alt: data.alt || '',
      stretched: data.stretched || false
    };
    this.readOnly = readOnly;
    this.wrapper = null;
    this.mediaUpload = (config && config.mediaUpload) || {};
    this._uploading = false;
  }

  render() {
    this.wrapper = document.createElement('div');
    this._render();
    return this.wrapper;
  }

  _spaceId() {
    return this.mediaUpload.spaceId || '';
  }

  _render() {
    const eu = escapeAttr;
    const urlVal = eu(this.data.url);
    const capVal = eu(this.data.caption);
    const altVal = eu(this.data.alt);

    this.wrapper.innerHTML = `
      <div class="image-block ${this.data.stretched ? 'w-full' : 'max-w-2xl mx-auto'}" data-testid="image-block">
        ${!this.readOnly ? `
          <div class="mb-2 flex flex-wrap items-center gap-2">
            <input type="text" class="url-input flex-1 min-w-[12rem] px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900" placeholder="Image URL or upload…" value="${urlVal}" />
            <input type="file" class="image-file-input hidden" accept="image/*" />
            <button type="button" class="image-upload-btn px-3 py-2 text-sm rounded border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" ${this._uploading ? 'disabled' : ''}>
              ${this._uploading ? 'Uploading…' : 'Upload'}
            </button>
            <label class="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <input type="checkbox" class="stretched-check" ${this.data.stretched ? 'checked' : ''} />
              Full width
            </label>
          </div>
          <p class="image-upload-err text-xs text-red-600 mb-2 hidden"></p>
        ` : ''}
        ${this.data.url ? `
          <img src="${urlVal}" alt="${altVal}" class="rounded-lg w-full max-h-[70vh] object-contain" />
          <div class="mt-2 ${!this.readOnly ? '' : 'text-center'}">
            ${!this.readOnly ? `
              <input type="text" class="caption-input w-full px-2 py-1 text-sm text-slate-500 bg-transparent border-0 focus:outline-none" placeholder="Add a caption..." value="${capVal}" />
              <input type="text" class="alt-input w-full px-2 py-1 text-xs text-slate-400 bg-transparent border-0 focus:outline-none" placeholder="Alt text..." value="${altVal}" />
            ` : this.data.caption ? `
              <p class="text-sm text-slate-500">${eu(this.data.caption)}</p>
            ` : ''}
          </div>
        ` : `
          <div class="p-8 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center text-slate-400 text-sm">
            Paste an image URL or click Upload
          </div>
        `}
      </div>
    `;

    if (!this.readOnly) {
      const errEl = this.wrapper.querySelector('.image-upload-err');
      const showErr = (msg) => {
        if (!errEl) return;
        errEl.textContent = msg || '';
        errEl.classList.toggle('hidden', !msg);
      };

      this.wrapper.querySelector('.url-input')?.addEventListener('input', (e) => {
        this.data.url = e.target.value;
        showErr('');
        this._render();
      });
      this.wrapper.querySelector('.caption-input')?.addEventListener('input', (e) => {
        this.data.caption = e.target.value;
      });
      this.wrapper.querySelector('.alt-input')?.addEventListener('input', (e) => {
        this.data.alt = e.target.value;
      });
      this.wrapper.querySelector('.stretched-check')?.addEventListener('change', (e) => {
        this.data.stretched = e.target.checked;
        this._render();
      });

      const fileInput = this.wrapper.querySelector('.image-file-input');
      const uploadBtn = this.wrapper.querySelector('.image-upload-btn');

      uploadBtn?.addEventListener('click', () => {
        showErr('');
        if (!canUploadMedia(this.mediaUpload)) {
          showErr('Uploads are not available in this context.');
          return;
        }
        fileInput?.click();
      });

      fileInput?.addEventListener('change', async () => {
        const file = fileInput.files && fileInput.files[0];
        fileInput.value = '';
        if (!file) return;
        this._uploading = true;
        this._render();
        try {
          const res = await performMediaUpload(this.mediaUpload, file, 'image');
          this.data.url = res.url || '';
          showErr('');
        } catch (e) {
          showErr(e.message || 'Upload failed');
        } finally {
          this._uploading = false;
          this._render();
        }
      });
    }
  }

  save() {
    return this.data;
  }
}

export default ImageBlock;
